import { useQuery } from "@tanstack/react-query";
import { Navigate } from "react-router-dom";
import { useAuth } from "../lib/useAuth";
import { TabLayout } from "../layouts/TabLayout";
import { LoadingState } from "../components/common/LoadingState";
import { ErrorState } from "../components/common/ErrorState";
import { apiFetch, USE_MOCK } from "../lib/api";
import { homeMock } from "../lib/mock/home";
import { SotdaHomeScreen } from "../redesign/live/HomeScreen";
import type { HomePageData } from "../types/home";

// 홈 데이터 — VITE_USE_MOCK=true 면 homeMock 전용, 아니면 apiFetch('/api/mobile/home')
// 세션 복원 완료 전엔 호출 보류 — 미인증 시점의 빈 200 응답이 캐시되는 것 방지
// (terra 버그13 — MbtiPage useMbtiData와 동일 enabled 패턴)
function useHomeData(enabled: boolean) {
  return useQuery<HomePageData>({
    queryKey: ["mobile", "home"],
    queryFn: () =>
      USE_MOCK ? Promise.resolve(homeMock) : apiFetch<HomePageData>("/api/mobile/home"),
    enabled,
  });
}

// 홈 — 클로드디자인 교체본(SotdaHomeScreen)으로 렌더.
// 기존 솟다 홈 컴포넌트(MbtiStripCard/HeroNetWorth/QuickRow/DonutAllocation/
// RecentTransactionsCard/MarketMarquee/AiInsightCard)는 SotdaHomeScreen으로 대체.
// 제외된 솟다 고유 섹션 기록: docs/specs/2026-05-22-sotda-home-redesign-swap-design.md §5
export function HomePage() {
  const { user, loading: authLoading } = useAuth();
  const { data, isLoading, error, refetch } = useHomeData(!authLoading);

  // 세션 복원 중 — 미인증 판정 전 깜빡임 방지
  if (authLoading) {
    return (
      <TabLayout page="home">
        <LoadingState />
      </TabLayout>
    );
  }
  // 미인증 → 로그인 화면 유도 (빈화면 방지: 세션 없으면 홈이 null이라 진입 경로가 막혔던 버그)
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // 첫 fetch 대기 (placeholderData 있으면 거의 안 보임)
  if (isLoading && !data) {
    return (
      <TabLayout page="home">
        <LoadingState />
      </TabLayout>
    );
  }

  // fetch 실패 + 표시할 데이터 0
  if (error && !data) {
    return (
      <TabLayout page="home">
        <ErrorState error={error} onRetry={() => refetch()} />
      </TabLayout>
    );
  }

  if (!data) return null;

  // empty/normal 모두 SotdaHomeScreen — 빈 데이터는 0·빈배열로 렌더.
  return (
    <TabLayout page="home">
      <SotdaHomeScreen data={data} />
    </TabLayout>
  );
}
